import axios from "axios";
import useAuthStore from "../../stores/authStore";

const api = axios.create({
  baseURL: `${import.meta.env.VITE_HOST}/api`,
  withCredentials: true,
});

api.interceptors.request.use((req) => {
  const config = /** @type {any} */ (req);
  if (config.skipAuth) {
    return req;
  }

  const token = useAuthStore.getState().accessToken || localStorage.getItem("token");
  if (token && !config.headers?.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

/** @type {((token: string) => void)[]} */
let refreshSubscribers = [];
let isRefreshing = false;

/**
 * @param {string} newAccessToken
 */
function onRefreshed(newAccessToken) {
  refreshSubscribers.forEach((callback) => callback(newAccessToken));
  refreshSubscribers = [];
}

api.interceptors.response.use(
  (response) => response,
  async (err) => {
    const originalRequest = err.config;

    if (err.response?.status === 401 && originalRequest && !originalRequest._retry && !originalRequest.skipAuth) {
      if (originalRequest.url?.includes("/auth/refresh")) {
        useAuthStore.getState().logout();
        return Promise.reject(err);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push(/** @param {string} token */ (token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post("/auth/refresh");
        const newToken = data?.data?.accessToken;

        useAuthStore.getState().setAccessToken(newToken);
        onRefreshed(newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        refreshSubscribers = [];
        useAuthStore.getState().logout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  }
);

export default api;
